/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { Component, EventEmitter, Inject, OnInit, Output } from '@angular/core';
import {
  IMPORT_SYSTEMS_FACADE_TOKEN,
  IImportSystemsFacade,
} from '../../../../../data-access/src/lib/domain/facades-models/import-systems-facade-interface';
import { GlobalConstants } from '../../constants/global-constants';
import { SystemManagerExportReturnCodes } from '../../../../../../shared/ui/src/lib/enums/system-manager-export-return-codes';
import { Subject, switchMap, take, takeUntil } from 'rxjs';
import { AlertStatus, IAlert } from "../../../../../../shared/ui/src/lib/components/alert/alert-types";

@Component({
  selector: 'import-systems',
  templateUrl: './import-systems.component.html',
  styleUrls: ['./import-systems.component.scss'],
  standalone: false
})
export class ImportSystemsComponent implements OnInit {
  @Output() close = new EventEmitter();

  //#region Public members

  public componentTitle = GlobalConstants.ImportSystemTitle;
  public alertMessage = GlobalConstants.CheckSystemName;
  public alertStatus = AlertStatus.Warning;
  public alertName = GlobalConstants.ErrorSystems;
  public inputCompleted = false;
  public importButtonName = GlobalConstants.ImportButton;
  public cancelButtonName = GlobalConstants.CancelButton;
  public areDuplicates = false;
  public showAlert = false;
  public systemsDictionary: { [key: string]: string } = {};
  public systemsErrors: { [key: string]: string } = {};
  public duplicateSystemsDictionary: { [key: string]: string } = {};
  public keys = Object.keys;
  public get alert(): IAlert {
    return {
      message: this.alertMessage,
      type: this.alertStatus,
      isDismissable: false,
    };
  }

  //#endregion

  //#region Private members

  private filePath: string;
  private systemsList: string[];
  private configurationSystems: string[];
  private duplicateDialogHeight = 700;
  private duplicateDialogWidth = 780;
  private nonDuplicateDialogHeight = 560;
  private nonDuplicateDialogWidth = 350;
  private readonly closed$ = new Subject<void>();

  //#endregion

  /**
   * Constructs a new instance of the ImportSystemsComponent
   * @param facade - The facade for the import systems
   */
  constructor(@Inject(IMPORT_SYSTEMS_FACADE_TOKEN) private facade: IImportSystemsFacade) {}

  //#region Public methods

  public ngOnDestroy(): void {
    this.closed$.next();
    this.closed$.complete();
  }

  /**
   * Angular lifecycle method that is called after the component is initialized.
   * This method is used for initialization logic.
   */
  public ngOnInit(): void {
    this.filePath = this.facade.getLocalStorageForImportSystemsFile();
    this.facade
      .getAllImportSystems(this.filePath)
      .pipe(
        takeUntil(this.closed$),
        switchMap((response) => {
          this.systemsList = response;
          return this.facade.getSystemsFromConfiguration();
        }),
        takeUntil(this.closed$)
      )
      .subscribe((response) => {
        this.configurationSystems = response;
        //Initialize the systems dictionaries and errors
        this.systemsList.forEach((system) => {
          this.systemsDictionary[system] = '';
          this.systemsErrors[system] = '';
        });
        this.systemsList
          .filter((system) =>
            this.configurationSystems ? this.configurationSystems.map((s) => s).includes(system) : false
          )
          .forEach((system) => {
            this.duplicateSystemsDictionary[system] = '';
          });

        this.areDuplicates = this.keys(this.duplicateSystemsDictionary).length > 0;
        this.showAlert = this.areDuplicates;

        //Resize window depending on if existing duplicate systems
        if (this.areDuplicates) {
          this.facade.windowResize(this.duplicateDialogHeight, this.duplicateDialogWidth);
        } else {
          this.facade.windowResize(this.nonDuplicateDialogHeight, this.nonDuplicateDialogWidth);
        }

        //Enable Import button if no duplicate systems
        this.inputCompleted = !this.areDuplicates;
      });
  }

  public setConfigurationSystems(value: string[]): void {
    this.configurationSystems = value;
  }

  /**
   * Handle the user input change for the new system names
   */
  public onUserInputChanged(systemName: string): void {
    const systemNewName = this.systemsDictionary[systemName];
    if (!/^[a-zA-Z]/.test(systemNewName)) {
      //Check if the new system name not start with alphabet
      //If it is, show error message
      this.systemsErrors[systemName] = this.getMessageError(GlobalConstants.Alphabet);
    } else if (systemNewName === systemName) {
      //Check if the new system name is same as the old system name
      //If it is, show error message
      this.systemsErrors[systemName] = this.getMessageError(GlobalConstants.Same);
    } else if (
      Object.entries(
        this.keys(this.systemsDictionary).filter(
          (key) => key === systemNewName || this.systemsDictionary[key] === systemNewName
        )
      ).length > 1
    ) {
      //Check if the new system name is a duplicate
      //If it is, show error message
      this.systemsErrors[systemName] = this.getMessageError(GlobalConstants.Duplicate);
    } else {
      //If the new system name is valid, clear the error message
      this.systemsErrors[systemName] = '';
      this.duplicateSystemsDictionary[systemName] = systemNewName;
    }

    //Enable Import button when all the new system names are valid
    this.inputCompleted =
      !Object.values(this.systemsErrors).some((value) => value) &&
      !Object.values(this.duplicateSystemsDictionary).some((value) => !value);
  }

  /**
   * Check if the system name is a duplicate
   * @param systemName - The system name to check
   * @returns - True if the system name is a duplicate, false otherwise
   */
  public isDuplicate(systemName: string): boolean {
    //Check if the system name is a duplicate systems
    return systemName in this.duplicateSystemsDictionary;
  }

  /**
   * Close the window
   */
  public closeWindow(): void {
    this.facade.closeWindow();
    this.close.emit();
  }

  /**
   * Dismiss the alert
   */
  public onDismissClick(): void {
    this.hideAlert();
  }

  /**
   * Import the systems from the selected file to configuration file
   */
  public import(): void {
    //Check if the new system names exists in the configuration
    Object.entries(this.systemsDictionary).forEach(([key, value]) => {
      if (value) {
        if (this.configurationSystems.find((system) => system === value)) {
          this.systemsErrors[key] = this.getMessageError(GlobalConstants.Exist);
        }
      }
    });

    //Enable Import button when all the new system names are valid
    this.inputCompleted = !Object.values(this.systemsErrors).some((value) => value);

    //If there are no duplicate systems name, import the systems
    if (this.inputCompleted) {
      //Hide the error alert
      this.hideAlert();

      //Import the systems
      this.importSystemsLogic();
    } else {
      this.showGeneralErrorAlert();
    }
  }

  /**
   * Remove spaces from the string
   * @param str - The string to remove spaces from
   * @returns - The string without spaces
   */
  public removeSpaces(str: string): string {
    return str.replace(/\s+/g, '');
  }

  //#endregion

  //#region Private methods

  /**
   * Get the error message based on the system error message
   * @param systemErrorMessage system error message to get the error message for
   * @returns the error message for the system error message
   */
  private getMessageError(systemErrorMessage: string): string {
    const errorMessage = {
      [GlobalConstants.Alphabet]: GlobalConstants.NameMustNotStartWithNumber,
      [GlobalConstants.Duplicate]: GlobalConstants.NameAlreadyInUse,
      [GlobalConstants.Exist]: GlobalConstants.SystemNameExistsInConfiguration,
      [GlobalConstants.Same]: GlobalConstants.NameAlreadyInUse,
    };
    return errorMessage[systemErrorMessage];
  }

  /**
   * Show the fail import alert
   */
  private showFailImportAlert(): void {
    this.showAlert = true;
    this.alertMessage = GlobalConstants.ImportSystemFailed;
    this.alertStatus = AlertStatus.Warning;
    this.alertName = GlobalConstants.ImportSystemTitle;
  }

  /**
   * Show the general error alert
   */
  private showGeneralErrorAlert(): void {
    this.showAlert = true;
    this.alertMessage = GlobalConstants.CheckSystemName;
    this.alertStatus = AlertStatus.Warning;
  }

  /**
   * Hide the error alert
   */
  private hideAlert(): void {
    this.showAlert = false;
    this.alertMessage = '';
  }

  /**
   * Import the systems to the configuration
   */
  private importSystemsLogic(): void {
    //Import the systems to the configuration
    let result;
    this.facade
      .importSystemsLogic(this.systemsDictionary, this.filePath)
      .pipe(take(1))
      .subscribe((response) => {
        result = response;
        if (result === SystemManagerExportReturnCodes.ImportSuccess) {
          //If the import is successful, close the window
          localStorage.removeItem(GlobalConstants.IMPORT_SYSTEMS_SUCCESS);
          localStorage.setItem(GlobalConstants.IMPORT_SYSTEMS_SUCCESS, 'true');
          this.closeWindow();
        } else {
          //If the import fails, show an error message
          this.showFailImportAlert();
        }
      });
  }

  //#endregion
}
